export type WindowPoint = { x: number; y: number };
export type WindowRect = { height: number; width: number; x: number; y: number };

/** Every coin is drawn at this size (pt) and shrinks as it drops into the drawer button. */
export const HAN_COIN_SIZE = 26;

/** Coins leave the source one after another, a few ms apart, so the spray reads as a spill. */
const BURST_STAGGER_MS = 14;
/** The spray: each coin pops out of the source and starts to fall back. */
export const HAN_COIN_BURST_MS = 420;
/** The coins hang in the air for a beat so the amount can be admired before it is banked. */
const HANG_MS = 110;
/** Coins land this far apart: close enough to read as a stream, far enough for each tick. */
const ARRIVAL_STAGGER_MS = 46;
const FLIGHT_MS = 540;
const FLIGHT_JITTER_MS = 140;

const MIN_COINS = 5;
const MAX_COINS = 18;

export type HanCoinFlight = {
  burstDelayMs: number;
  /** Where the coin is at the end of the burst, relative to `start`: a short ballistic arc. */
  burstGravity: number;
  burstVelocity: WindowPoint;
  /** The flight's quadratic Bézier control point, above the target so the coin drops into it. */
  control: WindowPoint;
  flightDelayMs: number;
  flightMs: number;
  /** Han this coin carries; the coins of a deposit add up to its amount exactly. */
  han: number;
  /** Turns about its vertical axis over the whole trip, so the coin flashes as it flips. */
  spins: number;
  start: WindowPoint;
  target: WindowPoint;
  /** A fixed in-plane tilt (deg), so the coins never all face the same way. */
  tilt: number;
};

/** mulberry32: deterministic per deposit, so a deposit replays identically (and tests can pin it). */
function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** More Han, more coins — sublinearly, so 8 Han is a handful and 88 a shower that never floods. */
export function hanCoinCount(han: number): number {
  return Math.min(
    MAX_COINS,
    Math.max(MIN_COINS, Math.round(4 + Math.sqrt(Math.max(0, han)) * 1.3)),
  );
}

/** Splits `han` into `count` whole coin values, the remainder on the first coins. */
export function splitHanAcrossCoins(han: number, count: number): number[] {
  const whole = Math.max(0, Math.round(han));
  const base = Math.floor(whole / count);
  const remainder = whole - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

/** Where a coin's burst ends, `progress` 0–1 into it. Shared with the overlay's worklet. */
export function hanCoinBurstPoint(flight: HanCoinFlight, progress: number): WindowPoint {
  "worklet";
  return {
    x: flight.start.x + flight.burstVelocity.x * progress,
    y:
      flight.start.y +
      flight.burstVelocity.y * progress +
      flight.burstGravity * progress * progress,
  };
}

/**
 * The choreography of one deposit, in the shape the best currency animations share: the coins
 * spray out of the source in a fountain, hang for a beat, then each flies a curved path that
 * rises above the drawer button and drops into it, arriving one after another so every landing
 * gets its own tick, clink and bump of the button.
 */
export function planHanCoinDeposit({
  han,
  seed,
  source,
  target,
}: {
  han: number;
  seed: number;
  source: WindowRect;
  target: WindowPoint;
}): { coins: HanCoinFlight[]; durationMs: number } {
  const random = createRandom(seed);
  const count = hanCoinCount(han);
  const values = splitHanAcrossCoins(han, count);
  // A wide source spills along its length; a small one from its center.
  const spreadX = Math.max(0, source.width - HAN_COIN_SIZE) * 0.8;
  const coins = values.map((value, index): HanCoinFlight => {
    const start = {
      x: source.x + source.width / 2 + (random() - 0.5) * spreadX,
      y: source.y + source.height / 2,
    };
    // Mostly upward, fanned ±63°: a fountain, not a ring.
    const angle = -Math.PI / 2 + (random() - 0.5) * 2.2;
    const speed = 70 + random() * 80;
    const burstVelocity = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    const burstGravity = 90;
    const burstEnd = {
      x: start.x + burstVelocity.x,
      y: start.y + burstVelocity.y + burstGravity,
    };
    const control = {
      x: target.x + (random() - 0.5) * 70,
      y: Math.max(-40, Math.min(burstEnd.y, target.y) - (70 + random() * 90)),
    };
    return {
      burstDelayMs: index * BURST_STAGGER_MS,
      burstGravity,
      burstVelocity,
      control,
      flightDelayMs: HAN_COIN_BURST_MS + HANG_MS + index * ARRIVAL_STAGGER_MS,
      flightMs: FLIGHT_MS + random() * FLIGHT_JITTER_MS,
      han: value,
      spins: 1.5 + random() * 1.5,
      start,
      target,
      tilt: (random() - 0.5) * 50,
    };
  });
  return {
    coins,
    durationMs: Math.max(...coins.map((coin) => coin.flightDelayMs + coin.flightMs)),
  };
}

/** What a deposit landing in the map sheet's wallet tells it (see `hanCoinDepositWalletDisplay`). */
export type HanCoinWalletDeposit = {
  /** The balances once the deposit has landed; `null` until read. */
  balanceAfter: { han: number; hanMiles: number } | null;
  han: number;
  hanMiles: number;
  /** The Han of the coins that have landed so far. */
  landedHan: number;
};

/**
 * The balances the map sheet's wallet shows while `deposit` lands in it: its Han counts up from the
 * balance before the deposit as each coin lands, and its HanMiles move to the new balance once the
 * deposit is banked. With no deposit, or its balances unknown, the wallet as it is. With reduced
 * motion, or no Han to fly in, the deposit is banked at once.
 */
export function hanCoinDepositWalletDisplay(
  wallet: { han: number; hanMiles: number },
  deposit: HanCoinWalletDeposit | undefined,
  isReducedMotion: boolean,
): { han: number; hanMiles: number } {
  if (deposit === undefined || deposit.balanceAfter === null) {
    return wallet;
  }
  const landedHan = isReducedMotion || deposit.han <= 0 ? deposit.han : deposit.landedHan;
  const { han, hanMiles } = deposit.balanceAfter;
  return {
    han: han - deposit.han + landedHan,
    hanMiles: landedHan >= deposit.han ? hanMiles : hanMiles - deposit.hanMiles,
  };
}
